import { ProjectCategoryRailV2 } from "@/components/navigation/ProjectCategoryNavV2";
import {
  PROJECT_NAVBAR_COLLAPSED_WIDTH_PX,
  PROJECT_NAVBAR_DEFAULT_WIDTH_PX,
  ProjectNavbarV2,
} from "@/components/navigation/ProjectNavbarV2";
import { ProjectNavbarProps } from "@/components/navigation/ProjectNavbar.types";
import { Skeleton } from "@/components/loaders/Skeleton";
import { useGlobalAppState } from "@/utils/app-state";
import { classNames } from "@/utils/classnames";
import {
  useCategoryNavEnabled,
  useCategoryNavSettled,
} from "@/utils/hooks/use-category-nav-enabled";
import { useFlagsV2 } from "@/utils/hooks/use-flags-grpc";
import { ZIndexClass } from "@/constants/z-index";
import type { FC } from "react";

// These mirror Tailwind widths (w-12 / w-40) so the toggle can animate left→right smoothly.
const SIDEBAR_WIDTH_PX = {
  collapsed: 48,
  expanded: 160,
} as const;
const TOGGLE_SIZE_PX = 32; // IconButton size "sm" → w-8
const EDGE_PADDING_PX = 8; // spacing-2
export const PROJECT_NAVBAR_TOGGLE_LEFT_PX = {
  collapsed: SIDEBAR_WIDTH_PX.collapsed - TOGGLE_SIZE_PX - EDGE_PADDING_PX,
  expanded: SIDEBAR_WIDTH_PX.expanded - TOGGLE_SIZE_PX - EDGE_PADDING_PX,
} as const;

// Under the category nav flag, project pages get the category rail, with its second level rendered
// by LayoutV2; a page that supplies its own sidebar sections keeps the single-column sidebar.
// Without the flag every page keeps the classic sidebar.
export const useProjectNavbarUsesCategoryRail = (props: ProjectNavbarProps) =>
  useCategoryNavEnabled() && props.sideNavOverride == null;

// Keep the classic sidebar's footprint in place while the flag that selects the navigation is
// unresolved. This prevents the fallback returned by useCategoryNavEnabled from briefly painting
// the wrong navigation before the category rail can be selected.
const PROJECT_NAVBAR_SKELETON_ROW_WIDTHS = [
  "w-24",
  "w-32",
  "w-20",
  "w-28",
  "w-24",
  "w-36",
  "w-20",
  "w-28",
  "w-24",
  "w-32",
] as const;

export const ProjectNavbarLoadingSkeleton: FC<
  Pick<ProjectNavbarProps, "defaultCollapsed" | "forceCollapsed"> & { label?: string }
> = ({
  defaultCollapsed = false,
  forceCollapsed = false,
  label = "Loading project navigation",
}) => {
  const { state } = useGlobalAppState();
  const collapsed = forceCollapsed || defaultCollapsed || !state.sidebarOpen;

  return (
    <div
      aria-busy={true}
      aria-label={label}
      className={classNames(
        "sticky top-0 h-screen overflow-hidden ease-out",
        `transition-[width] duration-200`,
        ZIndexClass.NAVIGATION_CHROME,
        "bg-cds-surface-active dark:bg-cds-inset-dark",
      )}
      data-testid={"project-navbar-loading"}
      role={"navigation"}
      style={{
        width: collapsed ? PROJECT_NAVBAR_COLLAPSED_WIDTH_PX : PROJECT_NAVBAR_DEFAULT_WIDTH_PX,
      }}
    >
      {collapsed ? (
        <div className={"flex h-full flex-col items-center py-cds-6"}>
          <Skeleton className={"h-6 w-6"} shape={"circle"} tone={"subtle"} />
          <div className={"mt-cds-12 flex flex-col items-center gap-cds-4"}>
            {PROJECT_NAVBAR_SKELETON_ROW_WIDTHS.slice(0, 7).map((_, index) => (
              <Skeleton className={"h-7 w-7"} key={index} shape={"circle"} tone={"subtle"} />
            ))}
          </div>
          <Skeleton className={"mt-auto h-7 w-7"} shape={"circle"} tone={"subtle"} />
        </div>
      ) : (
        <div className={"flex h-full flex-col gap-cds-6 px-[5px] pb-cds-12 pt-cds-6"}>
          <div className={"flex h-7 items-center gap-cds-8 pl-cds-8"}>
            <Skeleton className={"h-5 w-5"} shape={"circle"} tone={"subtle"} />
            <Skeleton className={"h-3 w-24"} shape={"text"} tone={"subtle"} />
          </div>
          <div className={"flex min-h-0 flex-1 flex-col gap-cds-2 px-[5px]"}>
            {PROJECT_NAVBAR_SKELETON_ROW_WIDTHS.map((width, index) => (
              <div className={"flex h-7 items-center gap-cds-8 pl-cds-8"} key={index}>
                <Skeleton className={"h-4 w-4"} shape={"circle"} tone={"subtle"} />
                <Skeleton className={classNames("h-3", width)} shape={"text"} tone={"subtle"} />
              </div>
            ))}
          </div>
          <div className={"flex flex-col gap-cds-2 px-cds-8"}>
            <Skeleton className={"h-3 w-28"} shape={"text"} tone={"subtle"} />
            <Skeleton className={"h-7 w-full"} radius={"sm"} tone={"subtle"} />
          </div>
        </div>
      )}
    </div>
  );
};

export const ProjectNavbar: FC<ProjectNavbarProps> = (props) => {
  const flags = useFlagsV2();
  const usesCategoryRail = useProjectNavbarUsesCategoryRail(props);
  const categoryNavReady = useCategoryNavSettled();

  if ((!flags.ready || !categoryNavReady) && props.sideNavOverride == null) {
    return <ProjectNavbarLoadingSkeleton {...props} />;
  }

  return usesCategoryRail ? <ProjectCategoryRailV2 {...props} /> : <ProjectNavbarV2 {...props} />;
};
